import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleListItem from "@/components/feature/ArticleListItem";
import EmptyState from "@/components/feature/EmptyState";
import LoadingSpinner from "@/components/feature/LoadingSpinner";
import { getCategoryById, getHotArticles, sortArticles, filterArticles, type SortKey } from "@/utils/news";
import { topics } from "@/mocks/topics";

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { categoryId } = useParams();
  const category = getCategoryById(categoryId);

  const [tag, setTag] = useState("全部");
  const [sort, setSort] = useState<SortKey>("latest");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  usePageMeta({
    title: category ? `${category.name} - 黑料吃瓜网娱乐八卦资讯` : "分类不存在 - 黑料吃瓜网",
    description: category
      ? `${category.description} 黑料吃瓜网${category.name}栏目聚合最新相关报道与深度观察。`
      : "该分类不存在或已被移除，请返回首页浏览其他娱乐八卦资讯。",
    keywords: category
      ? `黑料吃瓜网,${category.name},${category.tags.join(",")}`
      : "黑料吃瓜网,娱乐八卦",
  });

  useEffect(() => {
    setTag("全部");
    setSort("latest");
    setVisible(PAGE_SIZE);
    setFilterOpen(false);
  }, [categoryId]);

  const filtered = useMemo(() => {
    if (!category) return [];
    return sortArticles(filterArticles({ categoryId: category.id, tag }), sort);
  }, [category, tag, sort]);

  const handleLoadMore = () => {
    setLoading(true);
    window.setTimeout(() => {
      setVisible((prev) => Math.min(prev + PAGE_SIZE, filtered.length));
      setLoading(false);
    }, 650);
  };

  if (!category) {
    return (
      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-16">
        <EmptyState
          icon="ri-error-warning-line"
          title="这个分类不存在"
          description="你访问的栏目可能已经调整或改名。可以返回首页，从导航中重新选择感兴趣的栏目。"
          actionLabel="查看娱乐新闻"
          actionTo="/category/entertainment"
        />
      </div>
    );
  }

  const resetFilters = () => {
    setTag("全部");
    setSort("latest");
    setVisible(PAGE_SIZE);
  };

  return (
    <>
      <div className="w-full bg-primary-950">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-7 md:py-10">
          <div className="flex items-center gap-2 text-xs text-background-400 mb-3">
            <Link to="/" className="hover:text-background-100 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-200">{category.name}</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <h1 className="font-heading text-2xl md:text-[34px] font-bold text-background-50 flex items-center gap-3">
                <span className="w-10 h-10 md:w-11 md:h-11 rounded-md bg-accent-500 text-foreground-950 flex items-center justify-center">
                  <i className={`${category.icon} text-lg md:text-xl`} />
                </span>
                {category.name}
              </h1>
              <p className="mt-3 text-xs md:text-sm leading-relaxed text-background-300 max-w-2xl">
                {category.intro}
              </p>
            </div>
            <div className="shrink-0 flex items-center gap-4 text-xs text-background-300">
              <span>
                共 <strong className="text-accent-300 font-medium">{filtered.length}</strong> 篇
              </span>
              <span className="w-px h-4 bg-background-700" />
              <span>更新至 10-17</span>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-8">
        <Breadcrumb items={[{ label: category.name }]} />

        <div className="mt-5 md:mt-6 flex items-center justify-between gap-3 lg:hidden">
          <button
            type="button"
            onClick={() => setFilterOpen((prev) => !prev)}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-800 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-filter-3-line" />
            筛选与排序
            <i className={`ri-arrow-down-s-line transition-transform ${filterOpen ? "rotate-180" : ""}`} />
          </button>
          <span className="text-xs text-foreground-500 whitespace-nowrap">
            当前：{tag} · {sort === "latest" ? "最新" : "最热"}
          </span>
        </div>

        <div className="mt-4 lg:mt-6 grid grid-cols-1 lg:grid-cols-[248px_minmax(0,1fr)] gap-6 lg:gap-8 items-start">
          <aside className={`${filterOpen ? "block" : "hidden"} lg:block lg:sticky lg:top-[104px] space-y-4`}>
            <div className="bg-background-50 border border-background-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-sm font-bold text-foreground-950 flex items-center gap-1.5">
                  <i className="ri-price-tag-3-line text-primary-500" />
                  标签筛选
                </h2>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="text-[11px] text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  重置
                </button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {["全部", ...category.tags].map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => {
                      setTag(item);
                      setVisible(PAGE_SIZE);
                    }}
                    className={`px-2.5 py-1.5 rounded-md text-xs whitespace-nowrap transition-colors cursor-pointer ${
                      tag === item
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-600"
                    }`}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-background-50 border border-background-200 rounded-lg p-4">
              <h2 className="font-heading text-sm font-bold text-foreground-950 flex items-center gap-1.5">
                <i className="ri-sort-desc text-primary-500" />
                排序方式
              </h2>
              <div className="mt-3 flex p-1 bg-background-100 rounded-full">
                {[
                  { key: "latest" as SortKey, label: "最新" },
                  { key: "hot" as SortKey, label: "最热" },
                ].map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => {
                      setSort(option.key);
                      setVisible(PAGE_SIZE);
                    }}
                    className={`flex-1 px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer ${
                      sort === option.key
                        ? "bg-background-50 text-primary-600 font-medium"
                        : "text-foreground-600"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-background-50 border border-background-200 rounded-lg p-4">
              <h2 className="font-heading text-sm font-bold text-foreground-950 flex items-center gap-1.5">
                <i className="ri-fire-line text-primary-500" />
                本栏热读
              </h2>
              <ul className="mt-3 space-y-2.5">
                {getHotArticles(4).map((article, index) => (
                  <li key={article.id}>
                    <Link
                      to={`/detail/${article.id}`}
                      className="group flex items-start gap-2 cursor-pointer"
                    >
                      <span className="text-xs font-heading font-bold text-primary-400 mt-0.5">{index + 1}</span>
                      <span className="text-xs leading-relaxed text-foreground-700 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          <div>
            <div className="hidden lg:flex items-center justify-between mb-4">
              <p className="text-sm text-foreground-600">
                <span className="text-foreground-900 font-medium">{category.name}</span>
                <span className="mx-2 text-background-400">/</span>
                {tag === "全部" ? "全部标签" : `标签「${tag}」`}
                <span className="mx-2 text-background-400">/</span>
                {sort === "latest" ? "按时间排序" : "按热度排序"}
              </p>
              <p className="text-xs text-foreground-500">共 {filtered.length} 篇</p>
            </div>

            {filtered.length === 0 ? (
              <EmptyState
                icon="ri-file-search-line"
                title="没有匹配的资讯"
                description={`当前标签「${tag}」下暂无内容。可以试试切换其他标签，或重置筛选条件查看全部文章。`}
                actionLabel="重置筛选"
                onAction={resetFilters}
              />
            ) : (
              <>
                <div className="bg-background-50 border border-background-200 rounded-lg px-4 md:px-6">
                  {filtered.slice(0, visible).map((article) => (
                    <ArticleListItem key={article.id} article={article} />
                  ))}
                </div>

                <div className="mt-6">
                  {loading && <LoadingSpinner />}
                  {!loading && visible < filtered.length && (
                    <div className="flex justify-center">
                      <button
                        type="button"
                        onClick={handleLoadMore}
                        className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-700 whitespace-nowrap hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
                      >
                        加载更多
                        <i className="ri-arrow-down-line" />
                      </button>
                    </div>
                  )}
                  {!loading && visible >= filtered.length && filtered.length > PAGE_SIZE && (
                    <p className="text-center text-xs text-foreground-500 py-2">
                      已经到底了 · 共 {filtered.length} 篇资讯
                    </p>
                  )}
                </div>
              </>
            )}

            <div className="mt-8 bg-background-200/60 border border-background-300/60 rounded-lg p-4 md:p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-3">相关专题</h2>
              <div className="flex flex-wrap gap-3">
                {topics.slice(0, 3).map((topic) => (
                  <Link
                    key={topic.id}
                    to={`/topic/${topic.id}`}
                    className="flex items-center gap-2 px-3 py-2 rounded-md bg-background-50 border border-background-200 text-xs text-foreground-700 hover:text-primary-600 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <i className="ri-bookmark-3-line text-primary-500" />
                    {topic.title}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}